import { useCallback, useState } from 'react';
import { useNotify } from '../../../hooks';
import { useApiClient } from '../../../hooks/useRestAPIHooks';
import { useReportErrorToBugsnag } from '../../../util/bugsnagReporting';
import { Folder, ModelUtils, TemplatingItemSummary } from '../models';
import {
  getTemplatingItemsInFolder,
  getTemplatingItemSummariesForPrefix,
} from './getTemplatingItemsInFolder';
import { useInvalidateTemplatingItems } from './useInvalidateTemplatingItems';

const getAvailableCopyFolderName = (
  sourceFolderName: string,
  siblingFolderNames: Set<string>,
) => {
  let index = 1;
  let candidate = `${sourceFolderName} (copy)`;
  while (siblingFolderNames.has(candidate)) {
    index += 1;
    candidate = `${sourceFolderName} (copy ${index})`;
  }
  return candidate;
};

const createTemplatingItemAtPath = async (
  apiClient: ReturnType<typeof useApiClient>,
  sourceItem: TemplatingItemSummary,
  destinationPath: string,
) => {
  const name = destinationPath.split('/').filter(Boolean).pop() ?? '';
  const folder = ModelUtils.getParentFolder({ path: destinationPath });
  const itemToCreate = ModelUtils.createNewTemplatingItem({
    type: sourceItem.type,
    name,
    folder,
    impressionType:
      sourceItem.type === 'template' ? sourceItem.impressionType : undefined,
  });

  const { data } = await apiClient.request(
    'POST /v1/ai-templates',
    itemToCreate,
  );
  return ModelUtils.templatingItemDetailFromRawData(data.template);
};

const getTemplatingItemDetail = async (
  apiClient: ReturnType<typeof useApiClient>,
  templatingItem: TemplatingItemSummary,
) => {
  const { data } = await apiClient.request('GET /v1/ai-templates/:id', {
    id: templatingItem.id,
  });
  return ModelUtils.templatingItemDetailFromRawData(data.template);
};

export const useCopyTemplatingFolder = () => {
  const apiClient = useApiClient();
  const [notify] = useNotify();
  const reportError = useReportErrorToBugsnag();
  const invalidateTemplatingItems = useInvalidateTemplatingItems();
  const [isCopying, setIsCopying] = useState(false);

  const getSiblingFolderNames = useCallback(
    async (folder: Folder) => {
      const destinationParent = ModelUtils.getParentFolder(folder);
      const destinationSiblings = await getTemplatingItemSummariesForPrefix(
        apiClient,
        destinationParent.path,
      );
      const siblingFolderNames = new Set(
        destinationSiblings
          .filter(
            item =>
              item.type === 'folder' &&
              ModelUtils.hasSamePath(
                ModelUtils.getParentFolder(item),
                destinationParent,
              ),
          )
          .map(item => ModelUtils.getName(item)),
      );
      return { destinationParent, siblingFolderNames };
    },
    [apiClient],
  );

  const copyTemplatingFolder = useCallback(
    async (sourceFolder: Folder, destinationFolderName?: string) => {
      setIsCopying(true);
      try {
        const { destinationParent, siblingFolderNames } =
          await getSiblingFolderNames(sourceFolder);

        const requestedFolderName = destinationFolderName?.trim();
        if (
          requestedFolderName &&
          siblingFolderNames.has(requestedFolderName)
        ) {
          notify('A folder with that name already exists', 'error');
          return false;
        }
        const resolvedDestinationFolderName =
          requestedFolderName && requestedFolderName.length > 0
            ? requestedFolderName
            : getAvailableCopyFolderName(
                ModelUtils.getName(sourceFolder),
                siblingFolderNames,
              );
        const destinationFolder = ModelUtils.moveToFolder(
          ModelUtils.createFolder(resolvedDestinationFolderName),
          destinationParent,
        );
        const sourceTemplatingItems = await getTemplatingItemsInFolder(
          apiClient,
          sourceFolder,
        );

        const sourceTemplates = sourceTemplatingItems
          .filter(item => item.type === 'template')
          .sort((a, b) => ModelUtils.getLevel(a) - ModelUtils.getLevel(b));
        const sourceCompositeReportDefinitions = sourceTemplatingItems
          .filter(item => item.type === 'compositeReportDefinition')
          .sort((a, b) => ModelUtils.getLevel(a) - ModelUtils.getLevel(b));

        const copiedTemplateIdsByOriginalId = new Map<string, string>();

        for (const sourceTemplate of sourceTemplates) {
          const sourceTemplateDetail = await getTemplatingItemDetail(
            apiClient,
            sourceTemplate,
          );
          if (sourceTemplateDetail.type !== 'template') {
            continue;
          }

          const destinationPath = ModelUtils.reparent(
            sourceTemplate,
            sourceFolder,
            destinationFolder,
          ).path;
          const copiedTemplate = await createTemplatingItemAtPath(
            apiClient,
            sourceTemplate,
            destinationPath,
          );
          if (copiedTemplate.type !== 'template') {
            continue;
          }

          await apiClient.request('PATCH /v1/ai-templates/:id', {
            type: copiedTemplate.type,
            id: copiedTemplate.id,
            content: sourceTemplateDetail.content,
          });
          copiedTemplateIdsByOriginalId.set(
            sourceTemplateDetail.id,
            copiedTemplate.id,
          );
        }

        for (const sourceCompositeReportDefinition of sourceCompositeReportDefinitions) {
          const sourceCompositeReportDefinitionDetail =
            await getTemplatingItemDetail(
              apiClient,
              sourceCompositeReportDefinition,
            );
          if (
            sourceCompositeReportDefinitionDetail.type !==
            'compositeReportDefinition'
          ) {
            continue;
          }

          const destinationPath = ModelUtils.reparent(
            sourceCompositeReportDefinition,
            sourceFolder,
            destinationFolder,
          ).path;
          const copiedCompositeReportDefinition =
            await createTemplatingItemAtPath(
              apiClient,
              sourceCompositeReportDefinition,
              destinationPath,
            );
          if (
            copiedCompositeReportDefinition.type !== 'compositeReportDefinition'
          ) {
            continue;
          }

          await apiClient.request('PATCH /v1/ai-templates/:id', {
            type: copiedCompositeReportDefinition.type,
            id: copiedCompositeReportDefinition.id,
            sections: sourceCompositeReportDefinitionDetail.sections.map(
              section => ({
                templateId:
                  copiedTemplateIdsByOriginalId.get(section.templateId) ??
                  section.templateId,
              }),
            ),
          });
        }

        invalidateTemplatingItems(
          sourceFolder,
          destinationParent,
          destinationFolder,
        );
        notify('Folder copied', 'success');
        return true;
      } catch (error) {
        reportError(error);
        notify('Failed to copy folder', 'error');
        return false;
      } finally {
        setIsCopying(false);
      }
    },
    [
      apiClient,
      getSiblingFolderNames,
      invalidateTemplatingItems,
      notify,
      reportError,
    ],
  );

  return {
    copyTemplatingFolder,
    isCopying,
  };
};
